import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { skip } from 'rxjs/internal/operators/skip';
import { MedServiceHeaderRowItemSearchResult } from '../../../../core/api/mhsConfigurationApiClient';
import { ModalityLookup } from '../../../../core/context/modalities-lookup';
import { ProceduresService } from '../../services/procedures-service';
import { ProcedureFilters } from './procedures-filters';

@Component({
  selector: 'app-procedures-view',
  imports: [
    CommonModule,
    NzTableModule,
    NzFlexModule,
    NzInputModule,
    FormsModule,
    NzDropdownModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzModalModule,
  ],
  templateUrl: './procedures-view.html',
  styleUrl: './procedures-view.scss',
})
export class ProceduresViewComponent {
  readonly router = inject(Router);
  readonly messageService = inject(NzModalService);
  readonly proceduresService = inject(ProceduresService);
  readonly searchResult = signal<MedServiceHeaderRowItemSearchResult | null>(null);
  readonly loading = signal(true);
  readonly modalities = signal(ModalityLookup.modalities);
  readonly filters = signal<ProcedureFilters>({
    type: '',
    query: '',
  });
  total = 0;
  pageSize = 10;
  pageIndex = 1;

  readonly filterObservable = toObservable(this.filters).pipe(
    skip(1),
    debounceTime(500),
    distinctUntilChanged(),
  );

  constructor() {
    this.filterObservable.subscribe(() => {
      this.onQueryParamsChange({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sort: [],
        filter: [],
      });
    });
  }
  reload(): void {
    this.total = 1;
    this.pageIndex = 1;
    this.filters.update((f) => ({ ...f, query: '' }));
    this.searchResult.set(null);
  }

  queryChange(value: string): void {
    this.filters.update((f) => ({ ...f, query: value }));
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const sort = params.sort.find((item) => item.value !== null);
    this.pageIndex = params.pageIndex;
    this.pageSize = params.pageSize;
    this.loading.set(true);

    this.proceduresService
      .search(
        this.filters().type ?? undefined,
        undefined,
        undefined,
        this.filters().query,
        undefined,
        undefined,
        undefined,
        params.pageIndex,
        params.pageSize,
        sort?.key,
        sort?.value === 'ascend',
      )
      .subscribe({
        next: (result) => {
          this.searchResult.set(result);
          this.total = result.total ?? 0;
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Unable to load procedures.', error);
          this.loading.set(false);
        },
      });
  }

  detailsClick(id: number): void {
    this.router.navigate(['/procedure', id, 'details']);
  }
  createProcedureClick(): void {
    this.router.navigate(['/procedure', 'create']);
  }
  editProcedureClick(procedureId: number): void {
    this.router.navigate(['/procedure', procedureId, 'edit']);
  }
  editServiceProviderClick(serviceProviderId: number): void {
    this.router.navigate(['/service-providers', serviceProviderId, 'edit']);
  }
  unhideProcedureClick(procedureId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz odkryć procedurę?',
      nzContent: 'Procedura zostanie odkryta i będzie widoczna w systemie.',
      nzOkText: 'Tak, odkryj',
      nzCancelText: 'Anuluj',
      nzOkDanger: true,
      nzOnOk: () => {
        this.unhideProcedure(procedureId);
      },
    });
  }
  hideProcedureClick(procedureId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz ukryć procedurę?',
      nzContent: 'Procedura zostanie ukryta i nie będzie widoczna w systemie.',
      nzOkText: 'Tak, ukryj',
      nzCancelText: 'Anuluj',
      nzOkDanger: true,
      nzOnOk: () => {
        this.hideProcedure(procedureId);
      },
    });
  }
  hideProcedure(procedureId: number): void {
    this.loading.set(true);
    this.proceduresService.hide(procedureId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
  unhideProcedure(procedureId: number): void {
    this.loading.set(true);
    this.proceduresService.reveal(procedureId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
}
